import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as VerifyIcon,
  PlayArrow as StartIcon,
  Check as CompleteIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Activity, ActivityStatus, UserRole } from '../types';
import {
  getActivityById,
  updateActivityStatus,
  updateActivity,
  deleteActivity,
} from '../services/activities';
import ActivityForm from '../components/activities/ActivityForm';

const ActivityDetails: React.FC = () => {
  const { activityId } = useParams<{ activityId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        if (activityId) {
          const data = await getActivityById(activityId);
          setActivity(data);
        }
      } catch (err) {
        setError('Failed to load activity details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [activityId]);

  const handleStatusChange = async (newStatus: ActivityStatus) => {
    try {
      if (activity && activityId) {
        setLoading(true);
        const updatedActivity = await updateActivityStatus(activityId, newStatus);
        setActivity(updatedActivity);
      }
    } catch (err) {
      setError('Failed to update activity status');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDialogOpen = () => {
    setIsEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
  };

  const handleDeleteDialogOpen = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleActivityUpdate = async (updatedActivity: Activity) => {
    try {
      if (activityId) {
        setLoading(true);
        const result = await updateActivity(activityId, updatedActivity);
        setActivity(result);
        handleEditDialogClose();
      }
    } catch (err) {
      setError('Failed to update activity');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivityDelete = async () => {
    try {
      if (activityId) {
        setLoading(true);
        await deleteActivity(activityId);
        navigate('/activities');
      }
    } catch (err) {
      setError('Failed to delete activity');
      console.error(err);
    } finally {
      setLoading(false);
      handleDeleteDialogClose();
    }
  };

  const getStatusChipColor = (status: ActivityStatus) => {
    switch (status) {
      case ActivityStatus.PENDING:
        return 'default';
      case ActivityStatus.IN_PROGRESS:
        return 'primary';
      case ActivityStatus.COMPLETED:
        return 'success';
      case ActivityStatus.VERIFIED:
        return 'success';
      case ActivityStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: ActivityStatus) => {
    switch (status) {
      case ActivityStatus.PENDING:
        return 'Pending';
      case ActivityStatus.IN_PROGRESS:
        return 'In Progress';
      case ActivityStatus.COMPLETED:
        return 'Completed';
      case ActivityStatus.VERIFIED:
        return 'Verified';
      case ActivityStatus.CANCELLED:
        return 'Cancelled';
      default:
        return status;
    }
  };

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'CHORE':
        return 'Chore';
      case 'SPORT':
        return 'Sport';
      case 'ATHLETIC':
        return 'Athletic';
      case 'FINANCIAL':
        return 'Financial';
      case 'CUSTOM':
        return 'Custom';
      default:
        return category;
    }
  };

  const canEdit = () => {
    if (!user || !activity) return false;
    
    // Parents can edit any activity
    if (user.role === UserRole.PARENT) return true;
    
    // Teachers and coaches can edit activities they created
    if ((user.role === UserRole.TEACHER || user.role === UserRole.COACH) && 
        activity.userId === user.userId) {
      return true;
    }
    
    return false;
  };

  const canChangeStatus = () => {
    if (!user || !activity) return false;
    
    // Children can only start and complete their own activities
    if (user.role === UserRole.CHILD) {
      return activity.userId === user.userId;
    }
    
    // Parents can change status of any activity
    if (user.role === UserRole.PARENT) return true;
    
    // Teachers and coaches can change status of activities they created
    if ((user.role === UserRole.TEACHER || user.role === UserRole.COACH) && 
        activity.userId === user.userId) {
      return true;
    }
    
    return false;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box>
        <Typography variant="h6">Activity not found</Typography>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/activities')}
          sx={{ mt: 2 }}
        >
          Back to Activities
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/activities')}
        sx={{ mb: 2 }}
      >
        Back to Activities
      </Button>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" gutterBottom>
              {activity.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                label={getCategoryLabel(activity.category)}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={getStatusLabel(activity.status as ActivityStatus)}
                color={getStatusChipColor(activity.status as ActivityStatus)}
              />
              <Chip label={`${activity.points} points`} variant="outlined" />
            </Stack>
          </Box>

          {canEdit() && (
            <Box>
              <IconButton onClick={handleEditDialogOpen} color="primary">
                <EditIcon />
              </IconButton>
              <IconButton onClick={handleDeleteDialogOpen} color="error">
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6">Description</Typography>
            <Typography variant="body1" paragraph>
              {activity.description}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle1">Assigned Date</Typography>
            <Typography variant="body1">
              {new Date(activity.createdAt).toLocaleDateString()}
            </Typography>
          </Grid>

          {activity.dueDate && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Due Date</Typography>
              <Typography variant="body1">
                {new Date(activity.dueDate).toLocaleDateString()}
              </Typography>
            </Grid>
          )}

          {activity.completedDate && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1">Completed Date</Typography>
              <Typography variant="body1">
                {new Date(activity.completedDate).toLocaleDateString()}
              </Typography>
            </Grid>
          )}
        </Grid>

        {canChangeStatus() && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Update Status
            </Typography>
            <Stack direction="row" spacing={2}>
              {activity.status === ActivityStatus.PENDING && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<StartIcon />}
                  onClick={() => handleStatusChange(ActivityStatus.IN_PROGRESS)}
                >
                  Start Activity
                </Button>
              )}

              {activity.status === ActivityStatus.IN_PROGRESS && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CompleteIcon />}
                  onClick={() => handleStatusChange(ActivityStatus.COMPLETED)}
                >
                  Mark as Completed
                </Button>
              )}

              {(activity.status === ActivityStatus.COMPLETED && user?.role === UserRole.PARENT) && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<VerifyIcon />}
                  onClick={() => handleStatusChange(ActivityStatus.VERIFIED)}
                >
                  Verify Completion
                </Button>
              )}

              {(activity.status === ActivityStatus.PENDING || 
                activity.status === ActivityStatus.IN_PROGRESS) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => handleStatusChange(ActivityStatus.CANCELLED)}
                >
                  Cancel Activity
                </Button>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Activity</DialogTitle>
        <DialogContent>
          {activity && (
            <ActivityForm
              activity={activity}
              onSubmit={handleActivityUpdate}
              onCancel={handleEditDialogClose}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete Activity</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Are you sure you want to delete this activity? This action cannot be undone.
          </Alert>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button onClick={handleDeleteDialogClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button onClick={handleActivityDelete} variant="contained" color="error">
            Delete
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ActivityDetails;
